import { useMemo } from 'react';
import { DATA_ADAPTER } from '../config/adapters';
import { useData } from '../contexts/index';
import { RESULT_HISTORY_LIVE_LIMIT, mergeResultLikes } from '../core/lottery/resultHistory';
import {
  isPlatformLotterySource,
  resolveResultsFeedForHouse,
} from '../core/lottery/mergePlatformDrawResults';
import type { ResultLike } from '../core/rules/officialDrawResults';
import { resultsHistoryFloorDayKey } from '../core/rules/resultTurns';
import { useDataSync } from './useDataSync';
import { useRecentPlatformLotteryDraws } from './useRecentPlatformLotteryDraws';
import { useResultHistoryPages } from './useResultHistoryPages';
import { useServerClock } from './useServerClock';

/**
 * Live Hoy triples + paged history through the first recorded draw.
 * Stored docs are never deleted by this feed.
 */
export function useResultHistoryFeed(source: string | null | undefined) {
  const { adapter } = useData();
  const { now } = useServerClock();
  const houseId = String(adapter?.houseId ?? '');
  const platform = isPlatformLotterySource(source);
  const firebase = DATA_ADAPTER === 'firebase';
  const weekFloorDayKey = resultsHistoryFloorDayKey(now);

  const houseLive = useDataSync(adapter, 'results', [] as ResultLike[]);
  const { draws: platformLive, error: feedError, loading: feedLoading, retry: retryFeed } = useRecentPlatformLotteryDraws(
    platform ? source : null,
    RESULT_HISTORY_LIVE_LIMIT,
  );
  const paged = useResultHistoryPages({
    enabled: firebase,
    platform,
    source,
    houseId,
    weekFloorDayKey,
  });

  const liveRows = useMemo(() => {
    if (platform && firebase) {
      return resolveResultsFeedForHouse([], platformLive, source);
    }
    return houseLive;
  }, [platform, firebase, platformLive, source, houseLive]);

  const results = useMemo(
    () => mergeResultLikes([liveRows, ...paged.pages]),
    [liveRows, paged.pages],
  );

  return {
    results,
    feedError: platform && firebase ? feedError : null,
    feedLoading: platform && firebase ? feedLoading : false,
    retryFeed: platform && firebase ? retryFeed : () => undefined,
    loadMore: paged.loadMore,
    hasMore: paged.hasMore,
    loadingMore: paged.loadingMore,
    pageError: paged.pageError,
    reachedOrigin: !firebase || (!paged.hasMore && !paged.pageError && results.length > 0),
    platformOwned: platform && firebase,
  };
}
